import React, { useState, useMemo } from 'react';
import styles from './IdentityValidationSection.module.scss';
import IdentityTable from './IdentityTable.tsx';
import IdentityModal from './IdentityModal.tsx';
import Pagination from '../Shared/Pagination.tsx';
import EmptyState from '../Shared/EmptyState.tsx';
import LoadingSpinner from '../Shared/LoadingSpinner.tsx';

interface IdentityValidationSectionProps {
  users: any[];
  loading: boolean;
  onValidate: (userId: string, isValid: boolean) => void;
}

const ITEMS_PER_PAGE = 5;
type IdentityFilter = 'all' | 'pending' | 'approved' | 'rejected';

const IdentityValidationSection: React.FC<IdentityValidationSectionProps> = ({
  users,
  loading,
  onValidate
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [activeFilter, setActiveFilter] = useState<IdentityFilter>('all');

  const getIdentityStatus = (row: any): 'pending' | 'approved' | 'rejected' => {
    const nestedUser = row?.user ?? row?.jobBoard ?? row;
    const legacy = nestedUser?.identity_validated ?? null;
    if (legacy === true) return 'approved';
    if (legacy === false) return 'rejected';
    return 'pending';
  };

  const pendingUsersCount = useMemo(() => {
    return (users || []).filter((row) => getIdentityStatus(row) === 'pending').length;
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (activeFilter === 'all') return users || [];
    return (users || []).filter((row) => getIdentityStatus(row) === activeFilter);
  }, [users, activeFilter]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((filteredUsers || []).length / ITEMS_PER_PAGE));
  }, [filteredUsers]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return (filteredUsers || []).slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, filteredUsers]);

  const openModal = (user: any) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const handleFilterChange = (filter: IdentityFilter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  if (loading) {
    return <LoadingSpinner message="Cargando usuarios..." />;
  }

  if (!users || users.length === 0) {
    return (
      <EmptyState
        title="No hay solicitudes de validación"
        message="Todavía no hay usuarios para revisar"
      />
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <h2 className={styles.title}>Validación de Identidad</h2>
        <span className={styles.count}>
          {pendingUsersCount} {pendingUsersCount === 1 ? 'usuario' : 'usuarios'} pendiente{pendingUsersCount !== 1 ? 's' : ''}
        </span>
      </div>
      <div className={styles.filters}>
        <button
          type="button"
          className={`${styles.filterButton} ${activeFilter === 'all' ? styles.filterActive : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          Todos
        </button>
        <button
          type="button"
          className={`${styles.filterButton} ${activeFilter === 'pending' ? styles.filterActive : ''}`}
          onClick={() => handleFilterChange('pending')}
        >
          Pendientes
        </button>
        <button
          type="button"
          className={`${styles.filterButton} ${activeFilter === 'approved' ? styles.filterActive : ''}`}
          onClick={() => handleFilterChange('approved')}
        >
          Aprobados
        </button>
        <button
          type="button"
          className={`${styles.filterButton} ${activeFilter === 'rejected' ? styles.filterActive : ''}`}
          onClick={() => handleFilterChange('rejected')}
        >
          Rechazados
        </button>
      </div>

      <IdentityTable
        users={paginatedUsers}
        onViewImages={openModal}
        onValidate={onValidate}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {modalOpen && selectedUser && (
        <IdentityModal
          user={selectedUser}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default IdentityValidationSection;

